import React, { ReactNode, useMemo, useState } from 'react';
import CloseRounded from '@mui/icons-material/CloseRounded';
import { useDoranCalendar } from '@doranjs/react';
import {
    Dialog,
    DialogContent,
    IconButton,
    InputAdornment,
    TextField,
    Tooltip,
} from '@mui/material';

export const DoranModalTrigger = ({
    name,
    label,
    value,
    icon,
    disabled,
    invalid,
    helperText,
    open,
    onOpen,
    onClear,
    onBlur,
}: {
    name?: string;
    label?: string;
    value?: string;
    icon?: ReactNode;
    disabled?: boolean;
    invalid?: boolean;
    helperText?: ReactNode;
    open: boolean;
    onOpen: () => void;
    onClear?: () => void;
    onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
    ltr?: boolean;
}) => {
    const hasValue = Boolean(String(value ?? '').trim());
    const [focused, setFocused] = useState(false);
    const startAdornment = useMemo(
        () =>
            icon ? (
                <InputAdornment position="start">{icon}</InputAdornment>
            ) : undefined,
        [icon],
    );
    const endAdornment = useMemo(
        () =>
            hasValue && onClear && !disabled ? (
                <InputAdornment position="end">
                    <Tooltip title="پاک کردن">
                        <IconButton
                            size="small"
                            aria-label="پاک کردن تاریخ"
                            onMouseDown={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                            }}
                            onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                onClear();
                            }}>
                            <CloseRounded fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </InputAdornment>
            ) : undefined,
        [disabled, hasValue, onClear],
    );

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (
            event.key === 'Enter' ||
            event.key === ' ' ||
            event.key === 'ArrowDown'
        ) {
            event.preventDefault();
            if (!disabled) onOpen();
        }
    };

    return (
        <TextField
            fullWidth
            className="project-doran-trigger"
            name={name}
            variant="outlined"
            label={label || 'انتخاب تاریخ'}
            value={value || ''}
            disabled={Boolean(disabled)}
            error={Boolean(invalid)}
            helperText={helperText || undefined}
            onClick={() => {
                if (!disabled) onOpen();
            }}
            onFocus={() => setFocused(true)}
            onBlur={(event) => {
                setFocused(false);
                onBlur?.(event);
            }}
            InputLabelProps={{ shrink: open || focused || hasValue }}
            InputProps={{
                readOnly: true,
                startAdornment,
                endAdornment,
            }}
            inputProps={{
                'aria-haspopup': 'dialog',
                'aria-expanded': open,
                onKeyDown: handleKeyDown,
                style: { textAlign: 'left' },
            }}
            sx={{
                cursor: disabled ? 'not-allowed' : 'pointer',
                '& .MuiInputBase-root': {
                    cursor: disabled ? 'not-allowed' : 'pointer',
                },
                '& .MuiInputBase-input': {
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    userSelect: 'none',
                },
                '& .MuiOutlinedInput-root > input.MuiOutlinedInput-input': {
                    textAlign: 'left !important',
                },
            }}
        />
    );
};

/**
 * Project footer action backed by Doran's public calendar context.
 * It only changes the visible month; it never selects today. When the month/year
 * picker is open, it also returns the calendar to the day grid.
 */
export const DoranGoToToday = (): JSX.Element => {
    const { goToToday } = useDoranCalendar();

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        const calendar = event.currentTarget.closest('.doran-calendar');
        const activePickerButton = calendar?.querySelector<HTMLButtonElement>(
            '.doran-calendar__heading-btn--active',
        );

        // Doran owns the month/year panel state. Clicking its active heading
        // button closes that panel without changing the selected date.
        activePickerButton?.click();
        goToToday();
    };

    return (
        <button
            type="button"
            className="project-doran-go-today"
            onClick={handleClick}>
            برو به امروز
        </button>
    );
};

export const DoranModalDialog = ({
    open,
    title,
    onClose,
    children,
    wide = false,
}: {
    open: boolean;
    title?: string;
    onClose: () => void;
    children: ReactNode;
    wide?: boolean;
}) => (
    <Dialog
        open={open}
        onClose={onClose}
        maxWidth={false}
        className={`project-doran-dialog${
            wide ? ' project-doran-dialog--wide' : ''
        }`}
        PaperProps={{
            'aria-label': title || 'تقویم',
            sx: {
                bgcolor: 'transparent',
                backgroundImage: 'none',
                boxShadow: 'none',
                overflow: 'visible',
            },
        }}>
        <DialogContent className="project-doran-dialog__content">
            {children}
        </DialogContent>
    </Dialog>
);
